import { useCallback, useEffect, useState } from 'react';
import { CURRENCY_META, detectCurrencyFromTimezone, type Currency } from '../lib/pricing';

const STORAGE_KEY = 'gradelab.currency';

/**
 * Resolve the starting currency: localStorage override wins, otherwise we
 * guess from timezone. Runs once on mount; further changes via `setCurrency`
 * persist back to localStorage so the choice sticks across visits.
 */
function resolveInitial(): Currency {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Currency | null;
    if (stored && stored in CURRENCY_META) return stored;
  } catch {
    // localStorage can throw in strict-private-mode Safari; fall through.
  }
  return detectCurrencyFromTimezone();
}

/**
 * Shared currency state hook.
 *
 * All components that show a price (Pricing, Checkout, Billing) should read
 * from this hook so the currency pick is consistent across the app. We don't
 * wire it through React context because the value only changes when the user
 * explicitly picks one — re-render cost is negligible and the hook stays
 * drop-in simple.
 */
export function useCurrency() {
  const [currency, setCurrencyState] = useState<Currency>(resolveInitial);

  // Keep tabs in sync: if the user changes currency in one tab, other open
  // tabs pick it up via the storage event. Avoids the jarring experience of
  // having two tabs show different prices for the same plan.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && e.newValue in CURRENCY_META) {
        setCurrencyState(e.newValue as Currency);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  return { currency, setCurrency };
}
